import { Alert, Box, Button, Snackbar } from "@mui/material";
import { AlertProps } from "@mui/material/Alert";
import React, { createContext, useContext, useMemo, useState } from "react";
import LazyLottie from "src/shared/components/LazyLottie";

/** A single inline action rendered on the right of the snackbar (e.g. Undo). */
export type SnackbarAction = {
  label: string;
  onClick: () => void;
};

type SnackbarContextType = {
  alert: (
    severity: AlertProps["severity"],
    message: string,
    disableAutoHide?: boolean,
    action?: SnackbarAction
  ) => void;
  close: () => void;
  triggerConfetti: () => void;
};

const SnackbarContext = createContext<SnackbarContextType>(
  {} as SnackbarContextType
);

export function useSnackbarContext(): SnackbarContextType {
  return useContext(SnackbarContext);
}

export default function SnackbarProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [currentMessage, setCurrentMessage] = useState("");
  const [currentSeverity, setCurrentSeverity] =
    useState<AlertProps["severity"]>("info");
  const [autoHideOn, setAutoHideOn] = useState(true);
  const [currentAction, setCurrentAction] = useState<SnackbarAction | null>(
    null
  );
  const [showConfetti, setShowConfetti] = useState(false);

  function handleClose() {
    setAutoHideOn(true);
    setCurrentAction(null);
    setOpen(false);
  }

  const triggerConfetti = () => {
    setShowConfetti(true);
    setTimeout(() => {
      setShowConfetti(false);
    }, 3000);
  };

  const snackbarContext = useMemo(
    (): SnackbarContextType => ({
      alert: (severity, message, disableAutoHide, action) => {
        setAutoHideOn(!disableAutoHide);
        setCurrentSeverity(severity);
        setCurrentMessage(message);
        setCurrentAction(action ?? null);
        setOpen(true);
      },
      close: () => {
        handleClose();
      },
      triggerConfetti,
    }),
    []
  );

  return (
    <>
      <Snackbar
        open={open}
        autoHideDuration={autoHideOn ? 6000 : null}
        onClose={() => handleClose()}
      >
        <Alert
          severity={currentSeverity}
          action={
            currentAction ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  currentAction.onClick();
                  handleClose();
                }}
              >
                {currentAction.label}
              </Button>
            ) : undefined
          }
        >
          {currentMessage}
        </Alert>
      </Snackbar>
      {showConfetti && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 9999,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            pointerEvents: "none",
            overflow: "hidden",
          }}
        >
          <LazyLottie
            importFn={() => import("src/assets/Lotties/Confetti.json")}
            loop={false}
            style={{
              width: "100%",
              height: "100%",
              position: "absolute",
              top: 0,
              left: 0,
            }}
          />
        </Box>
      )}
      <SnackbarContext.Provider value={snackbarContext}>
        {children}
      </SnackbarContext.Provider>
    </>
  );
}
